import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Search, Plus, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", telephone: "", email: "", adresse: "", code_postal: "", ville: "" });
  const canCreate = ["admin", "planification"].includes(user.role);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit });
    if (search) p.set("search", search);
    try { const { data } = await api.get(`/clients?${p}`); setRows(data.items); setTotal(data.total); }
    finally { setLoading(false); }
  }, [page, search]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/clients", form); toast.success("Client créé"); setShowForm(false); setForm({ prenom: "", nom: "", telephone: "", email: "", adresse: "", code_postal: "", ville: "" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Clients</h1>
          <p className="text-sm text-slate-500">{total} client{total > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="client-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Rechercher…" className="w-52 rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-400" />
          </div>
          {canCreate && <button data-testid="client-add" onClick={() => setShowForm(true)} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Ajouter</button>}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-sm" data-testid="clients-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Nom</th><th className="px-4 py-2.5">Téléphone</th><th className="px-4 py-2.5">Adresse</th><th className="px-4 py-2.5">CP</th><th className="px-4 py-2.5">Ville</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="py-14 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></td></tr>
                : rows.length === 0 ? <tr><td colSpan={5} className="py-14 text-center text-slate-400">Aucun client</td></tr>
                : rows.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)} className="cursor-pointer border-b border-slate-100 hover:bg-blue-50/40">
                    <td className="px-4 py-2.5 font-medium text-slate-900">{c.prenom} {c.nom}</td>
                    <td className="px-4 py-2.5"><span className="mono text-xs text-slate-600">{c.telephone}</span></td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{c.adresse}</td>
                    <td className="px-4 py-2.5"><span className="mono text-xs">{c.code_postal}</span></td>
                    <td className="px-4 py-2.5 text-slate-600">{c.ville}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <span className="text-xs text-slate-500">Page {page} / {pages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <form onSubmit={create} className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" data-testid="client-form">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-bold">Nouveau client</h2><button type="button" onClick={() => setShowForm(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="grid grid-cols-2 gap-3">
              {[["prenom", "Prénom", true], ["nom", "Nom", true], ["telephone", "Téléphone"], ["email", "Email"], ["adresse", "Adresse"], ["code_postal", "Code postal"], ["ville", "Ville"]].map(([k, l, req]) => (
                <div key={k} className={k === "adresse" ? "col-span-2" : ""}>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{l}</label>
                  <input required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400" />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
              <button data-testid="client-form-submit" className="rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">Créer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
